import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { Filter } from '@src/app/shared/backend/clients/model/filter';
import { FilterRequestForCombo, SurveysService } from '@src/app/shared/backend/clients';
import { SurveyPostSaleRequest } from '@src/app/shared/backend/clients/model/surveyPostSaleRequest';

@Component({
    selector: 'app-dialog-survey-settings',
    templateUrl: './dialog-survey-settings.component.html',
    standalone: false
})
export class DialogSurveySettingsComponent implements OnDestroy {

  private modalboxVisible = false;

  @Input() get visible(): boolean {
    return this.modalboxVisible;
  }

  set visible(value: boolean) {
    const shouldLoadSurveyList = value && !this.modalboxVisible;

    this.modalboxVisible = value;

    if (shouldLoadSurveyList) {
      this.init();
    }
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() surveySettingsSave = new EventEmitter<SurveyPostSaleRequest>();

  filterOperator = FilterOperator;

  surveysList: any[] = [];
  surveysTotalRecords = 0;
  surveysRowsPerPageOption = 10;

  selectedSurveyId: any;

  private readonly componentDestroyed$ = new Subject<void>();

  public surveySettingsFormGroup: FormGroup = this.fb.group({
    survey: [[], [Validators.required]]
  });

  constructor(
    private surveysService: SurveysService,
    private fb: FormBuilder
  ) { }

  ngOnInit() {}

  private async init(): Promise<void> {
    const postSaleSurveyResponse: any = await firstValueFrom(this.surveysService.apiSurveysPostSaleGet());
    this.selectedSurveyId = postSaleSurveyResponse?.response ?? null;

    if (this.selectedSurveyId) {
      this.surveySettingsFormGroup.controls['survey'].setValue([this.selectedSurveyId]);
    } else {
      this.surveySettingsFormGroup.controls['survey'].setValue([]);
    }

    await this.getSurveys({ pageIndex: 1, search: '' });
  }

  onLazyLoadSurveys(event: any): void {
    this.getSurveys(event);
  }

  async getSurveys(event: any): Promise<void> {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const pageSize = Number(event?.pageSize) || this.surveysRowsPerPageOption;

    this.surveysRowsPerPageOption = pageSize;

    const additionalFilters: Filter[] = [
      {
        propertyName: 'isActive',
        operator: this.filterOperator.EQUALS,
        values: ['true']
      },
      {
        propertyName: 'isPublished',
        operator: this.filterOperator.EQUALS,
        values: ['true']
      }
    ];

    if (search) {
      additionalFilters.push({
        propertyName: 'name',
        operator: this.filterOperator.CONTAINS,
        values: [search]
      });
    }

    const filter: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: this.surveysRowsPerPageOption,
      additionalFilters
    };

    const surveysPage = await firstValueFrom(this.surveysService.apiSurveysFilterComboPost(filter));

    this.surveysList = surveysPage.results ?? [];
    this.surveysTotalRecords = surveysPage.totalCount ?? 0;

    if (this.selectedSurveyId) {
      const exists = this.surveysList.some((survey: any) => survey.id == this.selectedSurveyId);

      if (!exists) {
        const selectedSurvey = await firstValueFrom(this.surveysService.apiSurveysComboValueIdGet(this.selectedSurveyId));
        this.surveysList = [selectedSurvey, ...this.surveysList];
      }
    }
  }

  getSurveysTotalPages(): number {
    const total = Number(this.surveysTotalRecords) || 0;
    const pageSize = Number(this.surveysRowsPerPageOption) || 10;

    return Math.max(1, Math.ceil(total / pageSize));
  }

  onClose(): void {
    this.visibleChange.emit(false);
  }

  onSurveySettingsSave() {
    if (this.surveySettingsFormGroup.invalid) {
      this.surveySettingsFormGroup.markAllAsTouched();
      return;
    }

    this.surveySettingsSave.emit({
      surveyId: this.surveySettingsFormGroup.controls['survey'].value?.[0]
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
